<template>
  <div class="scan-result">
    <v-btn @click="textModal = true" color="accent" icon>
      <v-icon>mdi-cog</v-icon>
    </v-btn>
    <v-dialog scrollable v-model="textModal" width="600">
      <v-card class="dialog-form">
        <v-card-title class="headline">{{$t('scanresult_scan_response_text_settings')}}</v-card-title>
        <v-card-text>
          <div class="description font-weight-light pa-6">{{$t('scanresult_edit_text_description')}}</div>
          <div class="px-6">
            <span class="label font-weight-bold">{{$t('scanresult_scan_response_text')}} </span>
            <info-tooltip v-if="!isAvailable('editor:scan_result:text')" class="ml-3" :content="$t('scanresult_upgrade_unlock_feature')"></info-tooltip>
          </div>
          <div v-if="!isAvailable('editor:authentication')">
            <v-text-field class="pa-0 px-6" :disabled="!isAvailable('editor:scan_result:text') || !overrideAuthMessage" v-model="authMessageOverride.active" hide-details :placeholder="$t('scanresult_enter_customized_text')"></v-text-field>
          </div>
          <div v-if="isAvailable('editor:authentication')">
            <div class="px-6">
              <v-radio-group :disabled="!isAvailable('editor:scan_result:text')" v-model="overrideAuthMessage" class="inset ma-0" column>
                <v-radio :value="false" :label="$t('scanresult_default_scan_response_text')" class="py-2 ma-0">
                </v-radio>
                <v-radio :value="true" :label="$t('scanresult_customized_scan_response_text')" class="ma-0">
                </v-radio>
              </v-radio-group>
            </div>
            <div class="table pl-14">
              <div class="d-flex flex-row table-header">
                <div class="item font-weight-bold text-caption">{{$t('scanresult_default_text')}}</div>
                <div class="item font-weight-bold text-caption">{{$t('scanresult_customized_text')}}</div>
                <!-- <div class="item font-weight-medium text-caption">icon</div> -->
              </div>
              <div v-if="isAvailable('editor:authentication')" class="d-flex flex-row flex-grow-1 table-row pt-2">
                <div class="item py-2 font-weight-bold">{{$t('genuine')}}</div>
                <div class="item flex-grow-1">
                  <v-textarea class="ma-0 pa-0 pr-6" :disabled="!isAvailable('editor:scan_result:text') || !overrideAuthMessage" v-model="authMessageOverride.genuine" hide-details :placeholder="$t('scanresult_enter_customized_text')" rows="1" auto-grow></v-textarea>
                </div>
              </div>

              <div class="d-flex flex-row flex-grow-1 table-row pt-2">
                <div class="item py-2 font-weight-bold">{{$t('active_code')}}</div>
                <div class="item flex-grow-1">
                  <v-textarea class="ma-0 pa-0 pr-6" :disabled="!isAvailable('editor:scan_result:text') || !overrideAuthMessage" v-model="authMessageOverride.active" hide-details :placeholder="$t('scanresult_enter_customized_text')" rows="1" auto-grow></v-textarea>
                </div>
              </div>

              <div v-if="isAvailable('editor:authentication')" class="d-flex flex-row flex-grow-1 table-row pt-2">
                <div class="item py-2 font-weight-bold">{{$t('unable_to_verify')}}</div>
                <div class="item flex-grow-1">
                  <v-textarea class="ma-0 pa-0 pr-6" :disabled="!isAvailable('editor:scan_result:text') || !overrideAuthMessage" v-model="authMessageOverride.fake" hide-details :placeholder="$t('scanresult_enter_customized_text')" rows="1" auto-grow></v-textarea>
                </div>
              </div>

              <!-- <div v-if="isAvailable('editor.authentication')" class="d-flex flex-row flex-grow-1 table-row pt-2">
                <div class="item py-2 font-weight-bold">Active Code (Unsupported phone)</div>
                <div class="item flex-grow-1">
                  <v-text-field class="ma-0 pa-0 pr-6" :disabled="!isAvailable('editor.scan_result.text') || !overrideAuthMessage" v-model="authMessageOverride['active-unsupported']" hide-details placeholder="Enter a personalized text"></v-text-field>
                </div>
              </div> -->
            </div>
          </div>

          <div class="pa-6">
            <div class="label font-weight-bold">{{$t('scanresult_powered_by_scantrust')}}</div>
            <v-switch :disabled="!isAvailable('editor:scan_result:branding')" class="ma-0 pt-4" hide-details v-model="enableBranding" :label="`${$t('scanresult_display_brand_text')}: ${enableBranding ? $t('yes') : $t('no')}`"></v-switch>
          </div>

          <div class="pa-6 pt-3">
            <div class="label font-weight-bold">{{$t('styling')}}</div>
            <div v-for="status in statuses" :key="`${status.key}-${styleBarKey}`" class="style-status-block mt-3">
              <style-bar :css-keys="['fontSize', 'color', 'background', 'textAlign']" :default-font-size="24" :styles.sync="localAuthMessageStyles[status.key]">
                <template #actions>
                  <v-tooltip v-if="isAvailable('editor:tabs:tools:css')" top>
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on" @click="openCssEditor(status.key)" color="accent" class="mr-2">mdi-code-json</v-icon>
                    </template>
                    <span>{{$t('css_editor')}}</span>
                  </v-tooltip>
                </template>
              </style-bar>
              <div class="style-preview" :style="localAuthMessageStyles[status.key]" v-text="getPreviewText(status.key)"></div>
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="cancelChanges()" class="mr-4" text>{{$t('cancel')}}</v-btn>
          <v-btn color="primary" @click="saveChanges()" text>{{$t('save')}}</v-btn>
        </v-card-actions>
      </v-card>

      <v-dialog width="500" v-model="cssEditorModal">
        <v-card>
          <v-card-title class="headline">{{$t('css_editor')}}</v-card-title>
          <vue-json-editor class="fullscreen" v-model="cssEditorContent" mode="code" :expandedOnStart="true"></vue-json-editor>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="cssEditorModal = false">{{$t('cancel')}}</v-btn>
            <v-btn text color="primary" @click="saveCssEditor()">{{$t('save')}}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-dialog>
  </div>

</template>

<script>

import { mapGetters, mapMutations } from 'vuex'
import InfoTooltip from '@/components/utils/InfoTooltip'
import StyleBar from '@/components/utils/StyleBar'
import vueJsonEditor from 'vue-json-editor'
import { DEFAULT_AUTH_MESSAGE_COLORS } from '@/utils/default-stc-config'

export default {
  name: 'ScanResult',
  components: {
    InfoTooltip,
    StyleBar,
    vueJsonEditor
  },
  watch: {
    initialized (newVal) {
      if (newVal) {
        this.syncFromStore()
        this.enableLogo = !!(this.logo && this.logo.length)
      }
    },

    textModal (val) {
      if (val) {
        this.syncFromStore()
        this.enableBrandingBackup = this.enableBranding
        this.styleBarKey++
      }
    },

    overrideAuthMessage (newVal) {
      if (!newVal) {
        this.authMessageOverride = { genuine: '', active: '', fake: '', 'active-unsupported': '' }
      }
    },

    localAuthMessageStyles: {
      deep: true,
      handler (styles) {
        let cleared = false
        this.statuses.forEach(({ key }) => {
          const statusStyles = styles[key]
          if (statusStyles && statusStyles.clear) {
            this.$set(this.localAuthMessageStyles, key, {
              ...DEFAULT_AUTH_MESSAGE_COLORS[key],
              fontSize: '24px',
              textAlign: 'left'
            })
            cleared = true
          }
        })
        if (cleared) {
          this.$nextTick(() => { this.styleBarKey++ })
        }
      }
    }
  },
  computed: {
    ...mapGetters(['authMessage', 'authMessageStyles', 'initialized', 'isAvailable', 'previewResult']),

    enableBranding: {
      get () {
        return this.$store.getters.enableBranding
      },
      set (value) {
        this.setBrandingMode(value)
      }
    }

  },
  methods: {
    ...mapMutations({
      setDisplayMode: 'SET_PRODUCT_DISPLAY_MODE',
      setBrandingMode: 'SET_BRANDING_DISPLAY_MODE'
    }),

    syncFromStore () {
      this.authMessageOverride = JSON.parse(JSON.stringify(this.authMessage))
      this.authMessageOverrideBackup = JSON.parse(JSON.stringify(this.authMessage))
      this.overrideAuthMessage = this.isUsingCustomAuthMessage()
      this.overrideAuthMessageBackup = this.overrideAuthMessage
      this.localAuthMessageStyles = JSON.parse(JSON.stringify(this.authMessageStyles))
      this.localAuthMessageStylesBackup = JSON.parse(JSON.stringify(this.authMessageStyles))
    },

    isUsingCustomAuthMessage () {
      for (const [, val] of Object.entries(this.authMessageOverride)) {
        if (val && val.length) {
          return true
        }
      }
      return false
    },

    getPreviewText (status) {
      if (this.overrideAuthMessage && this.authMessageOverride[status] && this.authMessageOverride[status].length) {
        return this.authMessageOverride[status]
      }
      const statusObj = this.statuses.find(s => s.key === status)
      return statusObj ? this.$t(statusObj.label) : ''
    },

    saveChanges () {
      this.$store.dispatch('updateTabAuthMessage', this.authMessageOverride)
      this.statuses.forEach(({ key }) => {
        this.$store.dispatch('updateAuthMessageStyles', { status: key, styles: this.localAuthMessageStyles[key] })
      })
      this.textModal = false
    },

    cancelChanges () {
      this.authMessageOverride = JSON.parse(JSON.stringify(this.authMessageOverrideBackup))
      this.overrideAuthMessage = this.overrideAuthMessageBackup
      this.localAuthMessageStyles = JSON.parse(JSON.stringify(this.localAuthMessageStylesBackup))
      this.setBrandingMode(this.enableBrandingBackup)
      this.textModal = false
    },

    openCssEditor (status) {
      this.cssEditorStatus = status
      this.cssEditorContent = JSON.parse(JSON.stringify(this.localAuthMessageStyles[status] || {}))
      this.cssEditorModal = true
    },

    saveCssEditor () {
      this.$set(this.localAuthMessageStyles, this.cssEditorStatus, { ...this.cssEditorContent })
      this.cssEditorModal = false
      this.styleBarKey++
    }

  },
  data () {
    return {
      authMessageOverride: {},
      authMessageOverrideBackup: {},
      overrideAuthMessage: false,
      overrideAuthMessageBackup: false,
      defaultAuthMessage: {
        active: 'Active Code',
        genuine: 'Genuine',
        fake: 'Unable to Verify',
        'active-unsupported': 'Active Code'
      },
      enableMessage: false,
      textModal: false,
      displayScanResult: true,
      statuses: [
        { key: 'genuine', label: 'genuine' },
        { key: 'active', label: 'active_code' },
        { key: 'fake', label: 'unable_to_verify' }
      ],
      localAuthMessageStyles: {
        genuine: {},
        active: {},
        fake: {}
      },
      localAuthMessageStylesBackup: {
        genuine: {},
        active: {},
        fake: {}
      },
      styleBarKey: 0,
      enableBrandingBackup: false,
      cssEditorModal: false,
      cssEditorStatus: null,
      cssEditorContent: {}
    }
  },
  mounted () {
    this.syncFromStore()
  }
}

</script>

<style lang="sass" scoped>

@import '@/variables.sass'

.scan-result-area
  padding: 8px
  min-width: 265px
  border: 2px dashed rgba($pastel, 0.5)
  .scan-result-area-header
    width: 100%
    text-align: center
    color: rgba($pastel, 0.5)

.product-tool
  padding: 5px 10px
  min-height: 46px
  background: $bg-grey
  border-radius: 6px
  box-shadow: 0 2px 0 0 rgba(0, 0, 0, 0.3)

// ::v-deep .v-input--is-disabled
//   opacity: 1 !important

// ::v-deep .v-input--selection-controls__input
//   opacity: 0.6 !important

::v-deep label
  color: black !important

.dialog-form
  ::v-deep label
    color: rgba(0, 0, 0, 0.6) !important

.table
  .table-header
    border-bottom: 1px solid $border-grey
    .item
      width: 33.3%
      color: $label-grey
      text-transform: uppercase
      &:first-child
        max-width: 150px

  .table-row
    .item
      width: 33.3%
      &:first-child
        max-width: 150px
        color: $label-grey

.description
  font-size: 16px
  color: #333333

.style-status-block
  border-bottom: 1px solid #e0e0e0
  padding-bottom: 8px

.style-preview
  padding: 8px
  min-height: 28px
  line-height: 1.1
  font-size: 20px
  font-weight: bold
  white-space: pre-wrap

</style>
